<script setup lang="ts">
import { ref, onMounted, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { useToast } from "primevue/usetoast";
import Toolbar from "primevue/toolbar";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import SelectButton from "primevue/selectbutton";
import Button from "primevue/button";
import { useRecipeManagement } from "src/composables/useRecipeManagement";
import type { ViewMode } from "src/composables/useRecipeManagement";
import RecipeTable from "./RecipeTable.vue";
import ActionTable from "./ActionTable.vue";
import RecipeCreateDialog from "./RecipeCreateDialog.vue";
import RecipeEditDialog from "./RecipeEditDialog.vue";
import ActionCreateDialog from "./ActionCreateDialog.vue";
import ActionEditDialog from "./ActionEditDialog.vue";
import ActionLinkDialog from "./ActionLinkDialog.vue";
import ArticleLinkDialog from "./ArticleLinkDialog.vue";
import { CreateRecipeDTOType } from "src/api/models/createRecipeDTOType";
import type { CreateRecipeDTO } from "src/api/models/createRecipeDTO";
import type { CreateActionDTO } from "src/api/models/createActionDTO";
import type { UpdateActionDTO } from "src/api/models/updateActionDTO";
import type { FetchRecipeResponseLite } from "src/api/models/fetchRecipeResponseLite";
import type { NestedActionRecipeResponse } from "src/api/models/nestedActionRecipeResponse";

const toast = useToast();

const {
  viewMode,
  searchQuery,
  typeFilter,
  statusFilter,
  operationInProgress,
  recipeList, recipeListPage, recipeListTotalPages, recipeListLoading,
  actionList, actionListPage, actionListTotalPages, actionListLoading,
  expandedRecipe, expandedRecipeQueryLoading, expandedRecipeId,
  expandRecipe,
  loadRecipes, loadActions, loadCurrentView,
  handleCreateRecipe, handleCreateAction,
  handleUpdateAbstractAction, handleToggleStatus, handleDelete,
  handleLinkAction, handleRemoveAction, handleAddArticle, handleRemoveArticle,
  searchActionsForLink, searchArticles, fetchActionDetail,
} = useRecipeManagement();

const viewOptions = [
  { label: "Recipes", value: "recipes" as ViewMode },
  { label: "Actions", value: "actions" as ViewMode },
];

const typeOptions = [
  { label: "All types", value: null },
  ...Object.values(CreateRecipeDTOType).map((v) => ({
    label: v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: v,
  })),
];

const statusOptions = [
  { label: "All statuses", value: null },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const recipeTableRef = ref<InstanceType<typeof RecipeTable> | null>(null);

const showCreateRecipe = ref(false);
const showEditRecipe = ref(false);
const showCreateAction = ref(false);
const showEditAction = ref(false);
const showLinkAction = ref(false);
const showLinkArticle = ref(false);

const editingRecipe = ref<FetchRecipeResponseLite | null>(null);
const editingAction = ref<NestedActionRecipeResponse | null>(null);
const createActionRecipeId = ref<string | undefined>(undefined);
const linkActionRecipeId = ref<string>("");
const articleLinkTargetId = ref<string>("");
const articleLinkLabel = ref("Recipe");

const linkedActionIds = computed(() =>
  (expandedRecipe.value?.actions ?? []).map((a) => a.id).filter((id): id is string => !!id),
);
const linkedArticleIds = computed(() =>
  (expandedRecipe.value?.articles ?? []).map((a) => a.id).filter((id): id is string => !!id),
);

import { computed } from "vue";

const debouncedSearch = useDebounceFn(() => loadCurrentView(1), 300);

watch(searchQuery, () => debouncedSearch());
watch([typeFilter, statusFilter], () => loadCurrentView(1));
watch(viewMode, () => {
  expandRecipe(null);
  loadCurrentView(1);
});

onMounted(() => loadCurrentView());

async function onCreateRecipe(data: CreateRecipeDTO) {
  await handleCreateRecipe(data);
}

async function onCreateAction(data: CreateActionDTO) {
  await handleCreateAction(data, createActionRecipeId.value);
}

async function onEditRecipe(recipe: FetchRecipeResponseLite) {
  editingRecipe.value = recipe;
  showEditRecipe.value = true;
}

async function onUpdateRecipe(id: string, data: UpdateActionDTO) {
  await handleUpdateAbstractAction(id, data);
}

function onEditAction(action: NestedActionRecipeResponse) {
  editingAction.value = action;
  showEditAction.value = true;
}

async function onUpdateAction(id: string, data: UpdateActionDTO) {
  await handleUpdateAbstractAction(id, data);
}

async function onToggleStatus(id: string, currentStatus: string) {
  try { await handleToggleStatus(id, currentStatus); }
  catch { toast.add({ severity: "error", summary: "Error", detail: "Failed to toggle status", life: 3000 }); }
}

async function onDelete(id: string) {
  try { await handleDelete(id); }
  catch { toast.add({ severity: "error", summary: "Error", detail: "Failed to delete", life: 3000 }); }
}

function onCreateActionForRecipe(recipeId: string) {
  createActionRecipeId.value = recipeId;
  showCreateAction.value = true;
}

function onCreateStandaloneAction() {
  createActionRecipeId.value = undefined;
  showCreateAction.value = true;
}

function onLinkActionToRecipe(recipeId: string) {
  linkActionRecipeId.value = recipeId;
  showLinkAction.value = true;
}

async function onSubmitLinkAction(actionId: string) {
  try { await handleLinkAction(linkActionRecipeId.value, actionId); }
  catch { toast.add({ severity: "error", summary: "Error", detail: "Failed to link action", life: 3000 }); }
}

async function onRemoveAction(recipeId: string, actionId: string) {
  try { await handleRemoveAction(recipeId, actionId); }
  catch { toast.add({ severity: "error", summary: "Error", detail: "Failed to remove action", life: 3000 }); }
}

function onLinkArticle(targetId: string, label: string) {
  articleLinkTargetId.value = targetId;
  articleLinkLabel.value = label;
  showLinkArticle.value = true;
}

async function onRemoveArticle(abstractActionId: string, articleId: string) {
  try {
    await handleRemoveArticle(abstractActionId, articleId);
    recipeTableRef.value?.refetchActionDetail(abstractActionId);
  } catch {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to remove article", life: 3000 });
  }
}

async function onSubmitArticleLink(articleId: string) {
  try {
    await handleAddArticle(articleLinkTargetId.value, articleId);
    if (articleLinkLabel.value === "Action") {
      recipeTableRef.value?.refetchActionDetail(articleLinkTargetId.value);
    }
  } catch {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to link article", life: 3000 });
  }
}
</script>

<template>
  <div class="flex flex-col h-full">
    <Toolbar class="border-none border-b surface-ground px-4 py-3">
      <template #start>
        <div class="flex items-center gap-3">
          <SelectButton
            v-model="viewMode"
            :options="viewOptions"
            option-label="label"
            option-value="value"
            :allow-empty="false"
          />
          <span class="text-color-secondary">|</span>
          <InputText
            v-model="searchQuery"
            placeholder="Search by name..."
            class="w-64"
            size="small"
          />
          <Select
            v-model="typeFilter"
            :options="typeOptions"
            option-label="label"
            option-value="value"
            placeholder="Type"
            size="small"
            class="w-48"
          />
        </div>
      </template>
      <template #end>
        <div class="flex gap-2">
          <Button
            v-if="viewMode === 'recipes'"
            icon="pi pi-plus"
            label="New Recipe"
            severity="success"
            size="small"
            @click="showCreateRecipe = true"
          />
          <Button
            icon="pi pi-plus"
            :label="viewMode === 'recipes' ? 'New Action' : 'New Action'"
            :severity="viewMode === 'recipes' ? 'secondary' : 'success'"
            size="small"
            :outlined="viewMode === 'recipes'"
            @click="onCreateStandaloneAction"
          />
        </div>
      </template>
    </Toolbar>

    <div class="flex-1 overflow-auto">
      <RecipeTable
        ref="recipeTableRef"
        v-if="viewMode === 'recipes'"
        :recipes="recipeList"
        :loading="recipeListLoading"
        :disabled="operationInProgress"
        :page="recipeListPage"
        :total-pages="recipeListTotalPages"
        :expanded-recipe="expandedRecipe"
        :expanded-recipe-loading="expandedRecipeQueryLoading"
        :fetch-action-detail="fetchActionDetail"
        @page-change="loadRecipes"
        @expand="expandRecipe"
        @edit-recipe="onEditRecipe"
        @delete-recipe="onDelete"
        @toggle-status="onToggleStatus"
        @create-action="onCreateActionForRecipe"
        @link-action="onLinkActionToRecipe"
        @edit-action="onEditAction"
        @remove-action="onRemoveAction"
        @delete-action="onDelete"
        @toggle-action-status="onToggleStatus"
        @link-article-to-recipe="(id: string) => onLinkArticle(id, 'Recipe')"
        @link-article-to-action="(id: string) => onLinkArticle(id, 'Action')"
        @remove-article="onRemoveArticle"
      />

      <ActionTable
        v-else
        :actions="actionList"
        :loading="actionListLoading"
        :disabled="operationInProgress"
        :page="actionListPage"
        :total-pages="actionListTotalPages"
        @page-change="loadActions"
        @edit-action="onEditAction"
        @delete-action="onDelete"
        @toggle-status="onToggleStatus"
        @link-article="(id: string) => onLinkArticle(id, 'Action')"
      />
    </div>

    <RecipeCreateDialog v-model:visible="showCreateRecipe" :on-submit="onCreateRecipe" />
    <RecipeEditDialog
      v-model:visible="showEditRecipe"
      :recipe="editingRecipe as any"
      :on-submit="onUpdateRecipe"
    />
    <ActionCreateDialog v-model:visible="showCreateAction" :on-submit="onCreateAction" />
    <ActionEditDialog v-model:visible="showEditAction" :action="editingAction" :on-submit="onUpdateAction" />
    <ActionLinkDialog
      v-model:visible="showLinkAction"
      :search-fn="searchActionsForLink"
      :linked-action-ids="linkedActionIds"
      @submit="onSubmitLinkAction"
    />
    <ArticleLinkDialog
      v-model:visible="showLinkArticle"
      :search-fn="searchArticles"
      :linked-article-ids="linkedArticleIds"
      :entity-label="articleLinkLabel"
      @submit="onSubmitArticleLink"
    />
  </div>
</template>
